import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Popover, Box, Typography, Button, CircularProgress, Divider } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationDropdown } from './NotificationDropdown';
import { Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch, selectBadges } from '@/store';
import { updateBadge } from '@/store/updates-hub/updatesHubSlice';

export const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const badges = useAppSelector(selectBadges);
  const dispatch = useAppDispatch();
  const {
    notifications,
    loading,
    fetchNotifications,
    markAllAsRead,
  } = useNotifications();

  // Fetch recent unread notifications when popover opens
  useEffect(() => {
    if (anchorEl) {
      fetchNotifications({ status: 'unread', limit: 5 });
    }
  }, [anchorEl, fetchNotifications]);

  const handleBellClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    dispatch(updateBadge({ key: 'notifications', value: 0 })); // Reset notifications badge
    fetchNotifications({ status: 'unread', limit: 5 }); // Refresh list
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  // Use badge from Updates Hub
  const unreadCount = badges.notifications; 
  // Use fetched notifications for the dropdown list
  const recentUnreadNotifications = notifications;

  return (
    <>
      <IconButton color="inherit" onClick={handleBellClick} aria-describedby={id}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2,
            boxShadow: 3,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Сповіщення</Typography>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllAsRead}
                disabled={loading.action}
                size="small"
              >
                Позначити всі як прочитані
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {loading.notifications ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentUnreadNotifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Немає непрочитаних сповіщень.
            </Typography>
          ) : (
            <NotificationDropdown
              onClose={handleClose}
              notifications={recentUnreadNotifications}
              loading={loading.notifications}
            />
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button component={Link} to="/notifications" fullWidth sx={{ p: 1, textTransform: 'none' }} onClick={handleClose}>
              Переглянути всі сповіщення
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
